import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { apiClient } from "../lib/api";
import { toast } from "sonner";
import { Users, Plus, ArrowRight, Hash, Copy, Share, SignOut, Crown, Lock } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

const HERO_BG = "https://images.pexels.com/photos/6565248/pexels-photo-6565248.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Leagues() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreate = user?.is_premier || user?.is_admin;
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createName, setCreateName] = useState("");
  const [creating, setCreating] = useState(false);
  const [joinCode, setJoinCode] = useState(() => (params.get("code") || "").toUpperCase());
  const [joining, setJoining] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/leagues/me");
      setLeagues(data.leagues);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createLeague = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await apiClient.post("/leagues", {
        name: createName,
      });
      toast.success(`Created "${data.league.name}"`);
      setCreateName("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create league");
    } finally {
      setCreating(false);
    }
  };

  const joinLeague = async (e) => {
    e.preventDefault();
    setJoining(true);
    try {
      const { data } = await apiClient.post("/leagues/join", {
        invite_code: joinCode.trim().toUpperCase(),
      });
      toast.success(data.already_member ? "You're already in this league" : `Joined ${data.league.name}`);
      setJoinCode("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not join");
    } finally {
      setJoining(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Invite code copied");
  };

  const shareLink = (code) => {
    const url = `${window.location.origin}/leagues?code=${code}`;
    navigator.clipboard.writeText(url);
    toast.success("Share link copied — anyone with Pro can join");
  };

  const leaveLeague = async (id, name) => {
    if (!window.confirm(`Leave "${name}"? You can rejoin with the invite code.`)) return;
    try {
      await apiClient.post(`/leagues/${id}/leave`);
      toast.success(`Left ${name}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to leave");
    }
  };

  const proGate = false; // payment-gating removed

  return (
    <div>
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_BG})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#090A0C]/85 to-[#090A0C]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <span className="text-xs uppercase tracking-[0.25em] text-primary-yellow font-bold">Private Rooms</span>
          <h1 className="font-heading text-5xl sm:text-6xl uppercase font-black mt-3">Leagues</h1>
          <p className="text-white/60 mt-3 max-w-xl">Run a season-long battle with mates, colleagues or your fan club. Invite-only, own leaderboard.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
        {!canCreate ? (
          <div data-testid="leagues-paywall" className="surface-elevated border border-primary-yellow/30 glow-yellow p-10 max-w-2xl mx-auto text-center relative overflow-hidden">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-primary-yellow text-black text-[10px] uppercase tracking-[0.25em] font-bold">
              Premier Perk
            </span>
            <div className="mx-auto w-16 h-16 grid place-items-center bg-primary-yellow text-black rounded-full">
              <Crown size={32} weight="fill" />
            </div>
            <h2 className="font-heading text-4xl uppercase font-black mt-6">Private leagues are Premier</h2>
            <p className="text-white/70 mt-3 text-sm max-w-md mx-auto">
              Create your own league, invite mates, run a season-long battle on your own leaderboard. £9.99 unlocks the full season.
            </p>
            <ul className="mt-8 grid sm:grid-cols-2 gap-3 text-left text-sm max-w-md mx-auto">
              {[
                "Create unlimited private leagues",
                "Invite by shareable code",
                "Own leaderboard, own bragging rights",
                "Full season fixtures upfront",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2 text-white/80">
                  <Lock size={14} weight="bold" className="text-primary-yellow mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <button
              data-testid="leagues-upgrade-cta"
              onClick={() => navigate("/pricing")}
              className="mt-8 inline-flex items-center gap-2 bg-primary-yellow text-black px-6 py-4 text-sm uppercase tracking-[0.2em] font-bold hover:-translate-y-0.5 transition-transform"
            >
              <Crown size={14} weight="fill" /> Go Premier · £9.99
            </button>
          </div>
        ) : (
        <>
        <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {/* Create */}
          <form onSubmit={createLeague} data-testid="create-league-form" className="surface p-8">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-primary-yellow font-bold">
                <Plus size={12} weight="bold" /> New League
              </div>
              <h2 className="font-heading text-3xl uppercase font-black mt-3">Create a league</h2>
              <p className="text-white/60 text-sm mt-2">Premier perk · unlimited members · invite code only.</p>
              <div className="mt-6">
                <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2 font-semibold">League name</label>
                <input
                  data-testid="create-league-name"
                  required
                  minLength={2}
                  maxLength={60}
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="e.g. Pub Crew 2026"
                  className="w-full bg-[#0B0D11] border border-white/10 px-4 py-3 focus:border-primary-yellow focus:outline-none focus:ring-1 focus:ring-primary-yellow"
                />
              </div>
              <button
                data-testid="create-league-submit"
                type="submit"
                disabled={creating}
                className="mt-6 w-full bg-primary-yellow text-black uppercase tracking-[0.2em] font-bold py-4 text-sm hover:-translate-y-0.5 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Plus size={14} weight="bold" />
                {creating ? "Creating…" : "Create League"}
              </button>
            </form>

          {/* Join */}
          <form onSubmit={joinLeague} data-testid="join-league-form" className="surface p-8">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-primary-yellow font-bold">
              <Hash size={12} weight="bold" /> Join
            </div>
            <h2 className="font-heading text-3xl uppercase font-black mt-3">Got an invite code?</h2>
            <p className="text-white/60 text-sm mt-2">Enter the 8-character code shared by the creator.</p>
            <div className="mt-6">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2 font-semibold">Invite code</label>
              <input
                data-testid="join-league-code"
                required
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="XXXXXXXX"
                maxLength={12}
                className="w-full bg-[#0B0D11] border border-white/10 px-4 py-3 focus:border-primary-yellow focus:outline-none focus:ring-1 focus:ring-primary-yellow font-mono-num tracking-widest text-lg"
              />
            </div>
            <button
              data-testid="join-league-submit"
              type="submit"
              disabled={joining}
              className="mt-6 w-full border border-primary-yellow text-primary-yellow uppercase tracking-[0.2em] font-bold py-4 text-sm hover:bg-primary-yellow hover:text-black transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {joining ? "Joining…" : "Join League"}
            </button>
          </form>
        </div>

        {/* My leagues list */}
        <div>
          <h2 className="font-heading text-3xl uppercase font-black flex items-center gap-2">
            <Users weight="bold" /> Your Leagues
          </h2>
          {loading && <div className="mt-6 text-white/50 uppercase tracking-widest text-sm">Loading…</div>}
          {!loading && leagues.length > 0 && (
            <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
              {leagues.map((lg) => (
                <div key={lg.id} data-testid={`league-card-${lg.id}`} className="surface p-6 hover:bg-[#15171C] transition group">
                  <div className="flex items-start justify-between">
                    <h3 className="font-heading text-2xl uppercase font-black leading-tight">{lg.name}</h3>
                    {lg.is_creator && (
                      <span className="text-[10px] uppercase tracking-widest text-primary-yellow border border-primary-yellow/40 px-2 py-1">Owner</span>
                    )}
                  </div>
                  <div className="mt-4 flex items-center gap-3 text-xs text-white/60">
                    <span className="flex items-center gap-1"><Users size={12} /> {lg.member_count}</span>
                    <span>·</span>
                    <span>created {new Date(lg.expires_at).toLocaleDateString()}</span>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <button
                      onClick={() => copyCode(lg.invite_code)}
                      data-testid={`league-copy-${lg.id}`}
                      className="flex items-center justify-center gap-2 px-3 py-2 border border-white/10 hover:border-primary-yellow hover:text-primary-yellow transition text-xs font-mono-num tracking-widest"
                    >
                      <Copy size={12} weight="bold" /> {lg.invite_code}
                    </button>
                    <button
                      onClick={() => shareLink(lg.invite_code)}
                      data-testid={`league-share-${lg.id}`}
                      className="flex items-center justify-center gap-2 px-3 py-2 border border-white/10 hover:border-primary-yellow hover:text-primary-yellow transition text-xs uppercase tracking-widest"
                    >
                      <Share size={12} weight="bold" /> Share link
                    </button>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    {!lg.is_creator && (
                      <button
                        data-testid={`league-leave-${lg.id}`}
                        onClick={() => leaveLeague(lg.id, lg.name)}
                        className="flex-1 inline-flex items-center justify-center gap-2 border border-red-500/30 text-red-300 hover:bg-red-500/10 px-3 py-2 text-xs uppercase tracking-widest"
                      >
                        <SignOut size={12} weight="bold" /> Leave
                      </button>
                    )}
                    <Link
                      data-testid={`league-open-${lg.id}`}
                      to={`/leagues/${lg.id}`}
                      className={`${lg.is_creator ? "w-full" : "flex-1"} inline-flex items-center justify-center gap-1 bg-primary-yellow text-black uppercase tracking-widest text-[10px] font-bold px-4 py-2 hover:-translate-y-0.5 transition-transform`}
                    >
                      Open <ArrowRight size={12} weight="bold" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
}
